/**
 * Componente EquipmentStatusChart
 * 
 * Gráfico de status dos equipamentos.
 * Wrapper específico para o gráfico de pizza de status de equipamentos.
 * 
 * @module components/Dashboard/ChartsSection/EquipmentStatusChart
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FaTools } from 'react-icons/fa';
import GraficoPizza from '../../Charts/GraficoPizza';
import ChartWrapper from './ChartWrapper';
import chartConfig from '../../../utils/dashboard/chartConfig';

/**
 * Componente de gráfico de status dos equipamentos
 * 
 * Renderiza um gráfico de pizza mostrando a distribuição de equipamentos por status.
 * 
 * @param {object} props - Propriedades do componente
 * @param {Array} props.data - Dados do status dos equipamentos
 * @param {boolean} props.isLoading - Indica se está carregando
 * @param {Error|string|null} props.error - Erro ocorrido
 * @param {string} [props.className] - Classes CSS adicionais
 * @param {string} [props.testId] - ID para testes
 * 
 * @returns {JSX.Element} Componente de gráfico
 * 
 * @example
 * ```jsx
 * <EquipmentStatusChart
 *     data={equipmentStatusData}
 *     isLoading={isLoading}
 *     error={error}
 * />
 * ```
 */
const EquipmentStatusChart = ({
    data,
    isLoading,
    error,
    className = '',
    testId = null,
}) => {
    const title = (
        <>
            <FaTools className="mr-2 text-orange-500 inline" />
            Status dos Equipamentos
        </>
    );

    const subtitle = 'Distribuição entre Disponível, Em Uso e Manutenção.';

    return (
        <ChartWrapper
            isLoading={isLoading}
            error={error}
            data={data}
            loadingMessage="Carregando status dos equipamentos..."
            errorMessage="Erro ao carregar status dos equipamentos."
            emptyMessage="Não há dados de equipamentos para exibir."
            className={className}
            testId={testId || 'equipment-status-chart'}
        >
            <GraficoPizza
                data={data}
                dataKey={chartConfig.equipmentStatusConfig?.dataKey || 'total'}
                nameKey={chartConfig.equipmentStatusConfig?.nameKey || 'status'}
                title={title}
                subtitle={subtitle}
            />
        </ChartWrapper>
    );
};

EquipmentStatusChart.propTypes = {
    /** Dados do status dos equipamentos */
    data: PropTypes.array,
    
    /** Indica se está carregando */
    isLoading: PropTypes.bool.isRequired,
    
    /** Erro ocorrido (null se não houver) */
    error: PropTypes.oneOfType([
        PropTypes.instanceOf(Error),
        PropTypes.string,
        PropTypes.oneOf([null]),
    ]),
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
    
    /** ID para testes */
    testId: PropTypes.string,
};

EquipmentStatusChart.defaultProps = {
    data: null,
    error: null,
    className: '',
    testId: null,
};

export default EquipmentStatusChart;

